import { _decorator, v4, VideoClip, JsonAsset, Enum, warn, Director, director } from 'cc';
import { DEV, EDITOR } from 'cc/env';
import RemoteLoader from '../../Helper/Loading/RemoteLoader';
import VideoSprite from '../VideoSprite/VideoSprite';
import VapSocket from './Mix/VapSocket';
import { VapRemoteData, VapLocalData, IVapData, toUvArea } from './Data/VapData';
import Delay from '../../Utils/Delay';

export enum ResourceType {
    LOCAL,
    REMOTE,
}

const { ccclass, property } = _decorator;

@ccclass('VapVideoSprite')
export default class VapVideoSprite extends VideoSprite {
    @property({ type: Enum(ResourceType) })
    private _resourceType: ResourceType = ResourceType.LOCAL;

    @property(VideoClip)
    private _clip: VideoClip = null;

    @property
    private _remoteURL: string = '';

    @property
    private _remoteJsonConfigURL: string = '';

    @property
    private _localJsonConfig: JsonAsset = null;

    @property({ type: [VapSocket] })
    private _vapSockets: VapSocket[] = [];

    @property
    private readonly autoPlay: boolean = false;

    @property
    private readonly loop: boolean = false;

    private _isFirstEnabled: boolean = true;
    private _remoteJsonConfig: JsonAsset = null;
    private _vapData: IVapData = null;

    @property({ type: Enum(ResourceType) })
    public get resourceType(): ResourceType {
        return this._resourceType;
    }
    private set resourceType(value: ResourceType) {
        if (this._resourceType === value) {
            return;
        }
        this._resourceType = value;
    }

    @property({
        type: VideoClip,
        visible(this: VapVideoSprite) {
            return this.resourceType === ResourceType.LOCAL;
        },
    })
    public get clip(): VideoClip {
        return this._clip;
    }
    private set clip(value: VideoClip) {
        if (this._clip === value) {
            return;
        }
        this._clip = value;
    }

    @property({
        visible(this: VapVideoSprite) {
            return this.resourceType === ResourceType.REMOTE;
        },
    })
    public get remoteURL(): string {
        return this._remoteURL;
    }
    private set remoteURL(value: string) {
        if (this._remoteURL === value) {
            return;
        }
        this._remoteURL = value;
    }

    @property({
        visible(this: VapVideoSprite) {
            return this.resourceType === ResourceType.REMOTE;
        },
    })
    public get remoteJsonConfigURL(): string {
        return this._remoteJsonConfigURL;
    }
    private set remoteJsonConfigURL(val: string) {
        if (this._remoteJsonConfigURL === val) {
            return;
        }
        // clear cached config to allow re-fetching
        this._remoteJsonConfig = null;
        this._remoteJsonConfigURL = val;
    }

    @property({
        type: JsonAsset,
        visible(this: VapVideoSprite) {
            this._refreshInspector();
            return this.resourceType === ResourceType.LOCAL;
        },
    })
    public get localJsonConfig(): JsonAsset {
        return this._localJsonConfig;
    }
    private set localJsonConfig(val: JsonAsset) {
        if (this._localJsonConfig === val) {
            return;
        }
        this._localJsonConfig = val;
        this._refreshInspector();
    }

    @property({ type: [VapSocket], displayName: DEV && 'Vap掛點列表', tooltip: DEV && '設定Vap合成動畫掛點' })
    public get vapSockets() {
        return this._vapSockets;
    }
    public set vapSockets(val: VapSocket[]) {
        if (this._vapSockets === val) {
            return;
        }
        this._vapSockets = val;
        this._syncVapDataToSockets();
    }

    public onLoad() {
        super.onLoad();
        if (!this.videoElement) {
            this.videoElement = this._createVideoElement();
        }
        this._initVideoElement();
    }

    public start() {
        if (this.autoPlay) {
            this._updateSource();
            this.play();
        }
    }

    public async updateSource(data: VapLocalData | VapRemoteData) {
        this.resetVideoElementSetup();
        if ('videoUrl' in data) {
            this.resourceType = ResourceType.REMOTE;
            this.remoteURL = data.videoUrl;
            this.remoteJsonConfigURL = data.configUrl;
        } else {
            this.resourceType = ResourceType.LOCAL;
            this.clip = data.clip;
            this.localJsonConfig = data.jsonConfig;
        }
        this.resetTexture();
        await this._updateSource();
    }

    protected onEnable() {
        if (!this._isFirstEnabled && this.isPlaying) {
            Delay.event(Director.EVENT_BEFORE_UPDATE, director).then(() => {
                this._syncMaterialProperty();
            });
        } else {
            this._isFirstEnabled = false;
        }
    }

    protected updateFirstFrame() {
        super.updateFirstFrame();
        // sync material property after video metadata loaded to ensure correct display
        this._syncMaterialProperty();
        this.node.transform.setContentSize(this._vapData.info.w, this._vapData.info.h);
        this.currentTime = 0;
    }

    protected setupTexture(): void {
        super.setupTexture();
        this.vapSockets.forEach((socket) => socket.bindVideoTexture(this.sprite.spriteFrame));
    }

    protected updateTexture(): void {
        if (!this.shouldUpdateTexture) {
            return;
        }
        super.updateTexture();
        this.vapSockets.forEach((socket) => {
            if (socket.targetNode) {
                socket.targetNode.updateFrameData(this.currentTime);
            }
        });
    }

    private _createVideoElement() {
        const video = document.createElement('video') as HTMLVideoElement;
        return video;
    }

    private _initVideoElement() {
        // to allow remote video to be played successfully
        this.videoElement.crossOrigin = 'anonymous';
        this.videoElement.setAttribute('crossorigin', 'anonymous');
        this.videoElement.setAttribute('preload', 'auto');
        this.videoElement.setAttribute('webkit-playsinline', '');
        this.videoElement.setAttribute('playsinline', '');
        this.videoElement.muted = true;
        if (this.loop) {
            this.videoElement.setAttribute('loop', '');
        }
    }

    private async _updateSource() {
        let url: string;
        let config: JsonAsset;
        if (this.resourceType === ResourceType.REMOTE) {
            url = this.remoteURL;
            await this._fetchRemoteJsonConfig();
            config = this._remoteJsonConfig;
        } else {
            url = this.clip?.nativeUrl;
            config = this.localJsonConfig;
        }
        if (!url || !config) {
            return;
        }
        this._syncVapData(config.json as IVapData);
        this.videoElement.src = url;
    }

    private _syncVapData(vapData: IVapData) {
        this._vapData = vapData;
        this.vapSockets.forEach((socket) => socket.setVapData(vapData));
    }

    private _syncMaterialProperty() {
        const texW = this._vapData.info.videoW;
        const texH = this._vapData.info.videoH;
        const alphaUv = toUvArea(this._vapData.info.aFrame as any, texW, texH);
        const rgbUv = toUvArea(this._vapData.info.rgbFrame as any, texW, texH);
        const pass = this.sprite.material.passes[0];

        pass.setUniform(pass.getHandle('alphaFrameArea'), v4(...alphaUv));
        pass.setUniform(pass.getHandle('rgbFrameArea'), v4(...rgbUv));
    }

    private async _fetchRemoteJsonConfig() {
        if (!this.remoteJsonConfigURL) {
            return;
        }
        if (this._remoteJsonConfig) {
            return;
        }
        const json = await RemoteLoader.loadJson(this.remoteJsonConfigURL);
        if (!json) {
            return;
        }
        this._remoteJsonConfig = json;
    }

    private _refreshInspector() {
        if (!EDITOR || !this.localJsonConfig) {
            return;
        }
        this._syncVapDataToSockets(this.localJsonConfig.json as IVapData);
    }

    private _syncVapDataToSockets(specificVapData?: IVapData) {
        const data = specificVapData ?? this._vapData;
        if (!data) {
            return;
        }
        this.vapSockets.forEach((socket) => socket.setVapData(data));
        if (this.vapSockets.length && !data.src?.length) {
            warn('VapVideoSprite: VapData has no src object.');
        }
    }
}
